import React, { useState, useEffect } from 'react';
import { useUser } from '@clerk/clerk-react';
import { DatabaseService } from '../lib/database';
import ArchivedDealsPage from '../components/ArchivedDealsPage';

interface ArchivedDeal {
  deal_id: string;
  business_name: string;
  business_alias?: string;
  description?: string;
  creator: string;
  status: string;
  deal_value?: number;
  currency: string;
  created_at: string;
  updated_at: string;
  archived_at: string;
  archived_by: string;
  archive_reason?: string;
  can_restore: boolean;
}

export default function ArchivedDealsPageWrapper() {
  const { user } = useUser();
  const [archivedDeals, setArchivedDeals] = useState<ArchivedDeal[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadArchivedDeals = async () => {
      if (!user?.id) return;
      
      setIsLoading(true);
      try {
        const response = await DatabaseService.getArchivedDeals(user.id);
        if (response.data) {
          setArchivedDeals(response.data);
        }
      } catch (error) {
        console.error('Error loading archived deals:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadArchivedDeals();
  }, [user?.id]);

  const handleRestore = async (dealId: string) => {
    if (!user?.id) return;
    
    try {
      const response = await DatabaseService.restoreDeal(dealId, user.id);
      
      if (response.error) {
        alert(`Error restoring deal: ${response.error}`);
      } else {
        // Remove from archived deals list
        setArchivedDeals(prev => prev.filter(deal => deal.deal_id !== dealId));
        alert('Deal restored successfully');
      }
    } catch (error) {
      alert(`Error restoring deal: ${error}`);
    }
  };

  const handleDelete = async (dealId: string) => {
    if (!user?.id) return;
    
    try {
      const response = await DatabaseService.deleteDealCompletely(dealId, user.id);
      
      if (response.error) {
        alert(`Error deleting deal: ${response.error}`);
      } else {
        // Remove from archived deals list
        setArchivedDeals(prev => prev.filter(deal => deal.deal_id !== dealId));
        alert('Deal permanently deleted');
      }
    } catch (error) {
      alert(`Error deleting deal: ${error}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <ArchivedDealsPage
          archivedDeals={archivedDeals}
          onRestore={handleRestore}
          onDelete={handleDelete}
          isLoading={isLoading}
        />
      </div>
    </div>
  );
}
